import { Badge, Box, Button, Text, Flex, Select, Spinner } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { FiArrowDown, FiArrowUp } from "react-icons/fi";
import { api } from "../../../services/api";
import { DailySalesChart } from "../DailySalesChart";
import { PaymentMethodDonutChart } from "../PaymentMethodDonutChart";
import { DeliveryChart } from "../DeliveryChart";

const GREEN  = "#004a33";
const ORANGE = "#fc8f04";

const MONTHS = [
  { v: "01", l: "Janeiro" }, { v: "02", l: "Fevereiro" }, { v: "03", l: "Março" },
  { v: "04", l: "Abril" },   { v: "05", l: "Maio" },     { v: "06", l: "Junho" },
  { v: "07", l: "Julho" },   { v: "08", l: "Agosto" },   { v: "09", l: "Setembro" },
  { v: "10", l: "Outubro" }, { v: "11", l: "Novembro" }, { v: "12", l: "Dezembro" },
];

function pctChange(current, previous) {
  if (!previous) return null;
  return ((current - previous) / previous) * 100;
}

function DeltaBadge({ value }) {
  if (value === null || value === undefined || !isFinite(value)) return null;
  const isUp = value >= 0;
  return (
    <Flex align="center" gap="2px" fontSize="11px" fontWeight="700" color={isUp ? "#2f9e44" : "#e74c3c"}>
      {isUp ? <FiArrowUp size={10} /> : <FiArrowDown size={10} />}
      {Math.abs(value).toFixed(0)}% vs mês anterior
    </Flex>
  );
}

function StatCard({ label, count, amount, accent, delta }) {
  return (
    <Box
      bg="white" borderRadius="14px" p="16px 20px" flex="1" minW="170px"
      border="1px solid rgba(0,74,51,.08)" boxShadow="0 2px 10px rgba(0,74,51,.05)"
      borderLeft={`4px solid ${accent}`}
    >
      <Text fontSize="11px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.07em" mb="6px">
        {label}
      </Text>
      <Text fontSize="22px" fontWeight="900" color={GREEN} lineHeight="1.1">
        {amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
      </Text>
      <Flex align="center" justify="space-between" mt="4px">
        <Text fontSize="12px" color="gray.400">{count} pedido{count !== 1 ? "s" : ""}</Text>
        <DeltaBadge value={delta} />
      </Flex>
    </Box>
  );
}

function ConversionCard({ paidCount, openCount }) {
  const total = paidCount + openCount;
  const rate = total > 0 ? (paidCount / total) * 100 : 0;
  return (
    <Box
      bg="white" borderRadius="14px" p="16px 20px" flex="1" minW="170px"
      border="1px solid rgba(0,74,51,.08)" boxShadow="0 2px 10px rgba(0,74,51,.05)"
      borderLeft="4px solid #2980b9"
    >
      <Text fontSize="11px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.07em" mb="6px">
        Taxa de conversão
      </Text>
      <Text fontSize="22px" fontWeight="900" color={GREEN} lineHeight="1.1">{rate.toFixed(0)}%</Text>
      <Box bg="rgba(41,128,185,.12)" borderRadius="99px" h="6px" mt="8px" overflow="hidden">
        <Box bg="#2980b9" h="100%" borderRadius="99px" w={`${rate}%`} transition="width 0.3s" />
      </Box>
      <Text fontSize="11px" color="gray.400" mt="6px">{paidCount} de {total} pedidos pagos</Text>
    </Box>
  );
}

function Section({ title, children }) {
  return (
    <Box bg="white" borderRadius="16px" border="1px solid rgba(0,74,51,.08)" boxShadow="0 2px 12px rgba(0,74,51,.05)" p="18px" mb="16px">
      <Text fontSize="12px" fontWeight="800" color={GREEN} textTransform="uppercase" letterSpacing="0.06em" mb="14px">
        {title}
      </Text>
      {children}
    </Box>
  );
}

function previousPeriod(year, month) {
  let prevMonth = month - 1;
  let prevYear = year;
  if (prevMonth < 1) { prevMonth = 12; prevYear = year - 1; }
  return { prevYear, prevMonth };
}

export function Sales() {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(String(now.getMonth() + 1).padStart(2, "0"));
  const [selectedYear, setSelectedYear]   = useState(String(now.getFullYear()));
  const [allOrders, setAllOrders]     = useState([]);
  const [payments, setPayments]       = useState([]);
  const [batches, setBatches]         = useState([]);
  const [products, setProducts]       = useState([]);
  const [prevOrders, setPrevOrders]   = useState([]);
  const [coupons, setCoupons]         = useState([]);
  const [loading, setLoading]         = useState(false);

  const minYear = 2023;
  const maxYear = now.getFullYear();
  const yearOptions = Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);

  async function fetchPeriod(year, month) {
    const params = new URLSearchParams({ year, month });
    const response = await api.get(`/api/v1/batch_orders?${params}`);
    return response.data;
  }

  async function fetchOrders(year, month) {
    setLoading(true);
    try {
      const { prevYear, prevMonth } = previousPeriod(year, month);
      const [current, previous] = await Promise.all([
        fetchPeriod(year, month),
        fetchPeriod(prevYear, prevMonth),
      ]);
      setAllOrders(current.batch_orders || []);
      setBatches(current.batches || []);
      setProducts(current.products || []);
      setPayments(current.payments || []);
      setPrevOrders(previous.batch_orders || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchCoupons() {
    try {
      const response = await api.get("/api/v1/admin/coupons");
      setCoupons(response.data.coupons || []);
    } catch (error) {
      console.error("Error fetching coupons:", error);
    }
  }

  useEffect(() => {
    fetchOrders(parseInt(selectedYear, 10), parseInt(selectedMonth, 10));
    fetchCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paidOrders = allOrders.filter(o => o.paid);
  const openOrders = allOrders.filter(o => !o.paid && o.delivery_status !== "cancelled");
  const paidAmount = paidOrders.reduce((t, o) => t + o.amount, 0);
  const openAmount = openOrders.reduce((t, o) => t + o.amount, 0);
  const avgOrderValue = paidOrders.length > 0 ? paidAmount / paidOrders.length : 0;

  const prevPaid = prevOrders.filter(o => o.paid);
  const prevPaidAmount = prevPaid.reduce((t, o) => t + o.amount, 0);
  const prevOpenAmount = prevOrders.filter(o => !o.paid && o.delivery_status !== "cancelled").reduce((t, o) => t + o.amount, 0);
  const prevAvgOrderValue = prevPaid.length > 0 ? prevPaidAmount / prevPaid.length : 0;

  // Top produtos por faturamento (pagos) no período.
  const topProducts = useMemo(() => {
    const byProduct = {};
    paidOrders.forEach(o => {
      const batch = batches.find(b => b.id === o.batch_id);
      const product = products.find(p => p.id === batch?.product_id);
      if (!product) return;
      byProduct[product.id] ||= { name: product.name, revenue: 0, quantity: 0 };
      byProduct[product.id].revenue += o.amount;
      byProduct[product.id].quantity += o.quantity || 0;
    });
    return Object.values(byProduct).sort((a, b) => b.revenue - a.revenue).slice(0, 8);
  }, [paidOrders, batches, products]);
  const maxProductRevenue = topProducts[0]?.revenue || 1;

  // Cupons usados no período — só contagem de uso, sem tentar reconstituir o
  // valor exato descontado (não fica armazenado no Payment, só o coupon_id).
  const couponUsage = useMemo(() => {
    const byCoupon = {};
    payments.forEach(p => {
      if (!p.coupon_id || p.status !== "paid") return;
      byCoupon[p.coupon_id] = (byCoupon[p.coupon_id] || 0) + 1;
    });
    return Object.entries(byCoupon)
      .map(([couponId, count]) => ({ coupon: coupons.find(c => c.id === Number(couponId)), count }))
      .filter(c => c.coupon)
      .sort((a, b) => b.count - a.count);
  }, [payments, coupons]);

  const periodLabel = `${MONTHS.find(m => m.v === selectedMonth)?.l} ${selectedYear}`;
  const year = parseInt(selectedYear, 10);
  const month = parseInt(selectedMonth, 10);

  return (
    <Box>
      {/* Header */}
      <Box mb="24px">
        <Text fontSize="11px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.1em" mb="4px">
          Gestão de pedidos
        </Text>
        <Text fontSize="24px" fontWeight="900" color={GREEN}>Relatório de Vendas</Text>
      </Box>

      {/* Filter */}
      <Box
        bg="white" borderRadius="14px" p="16px 20px" mb="18px"
        border="1px solid rgba(0,74,51,.08)" boxShadow="0 2px 10px rgba(0,74,51,.05)"
      >
        <Text fontSize="12px" fontWeight="700" color="gray.500" mb="10px">Filtrar período</Text>
        <Flex gap="10px" align="flex-end" flexWrap="wrap">
          <Box flex="1" minW="120px">
            <Text fontSize="11px" color="gray.500" mb="4px" fontWeight="600">Mês</Text>
            <Select size="sm" borderRadius="8px" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
              {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </Select>
          </Box>
          <Box flex="1" minW="100px">
            <Text fontSize="11px" color="gray.500" mb="4px" fontWeight="600">Ano</Text>
            <Select size="sm" borderRadius="8px" value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </Select>
          </Box>
          <Button
            size="sm" borderRadius="8px" h="32px" px="16px"
            bg={GREEN} color={ORANGE} fontWeight="800" fontSize="12px"
            _hover={{ opacity: 0.88 }}
            onClick={() => fetchOrders(year, month)}
            isLoading={loading}
          >
            Filtrar
          </Button>
          <Text fontSize="11px" color="gray.400" ml="4px">{periodLabel}</Text>
        </Flex>
      </Box>

      {loading ? (
        <Flex justify="center" py="60px">
          <Spinner size="xl" color={GREEN} thickness="3px" />
        </Flex>
      ) : (
        <>
          {/* Stat cards */}
          <Flex gap="16px" flexWrap="wrap" mb="20px">
            <StatCard
              label="Pedidos pagos" count={paidOrders.length} amount={paidAmount} accent={GREEN}
              delta={pctChange(paidAmount, prevPaidAmount)}
            />
            <StatCard
              label="Pedidos em aberto" count={openOrders.length} amount={openAmount} accent={ORANGE}
              delta={pctChange(openAmount, prevOpenAmount)}
            />
            <StatCard
              label="Ticket médio" count={paidOrders.length} amount={avgOrderValue} accent="#8e44ad"
              delta={pctChange(avgOrderValue, prevAvgOrderValue)}
            />
            <ConversionCard paidCount={paidOrders.length} openCount={openOrders.length} />
          </Flex>

          {/* Vendas diárias */}
          <Section title={`Vendas diárias — ${periodLabel}`}>
            <DailySalesChart orders={allOrders} year={year} month={month} />
          </Section>

          {/* Forma de pagamento + Entrega */}
          <Flex gap="16px" flexWrap="wrap" mb="0">
            <Box flex="1" minW="280px">
              <Section title="PIX vs Cartão no período">
                <PaymentMethodDonutChart payments={payments} />
              </Section>
            </Box>
            <Box flex="1" minW="280px">
              <Section title="Entrega vs Retirada no período">
                <DeliveryChart orders={allOrders} />
              </Section>
            </Box>
          </Flex>

          {/* Top produtos */}
          <Section title="Top produtos no período (faturamento)">
            {topProducts.length === 0 ? (
              <Text fontSize="12px" color="gray.400">Nenhuma venda paga neste período.</Text>
            ) : (
              <Box display="flex" flexDirection="column" gap="10px">
                {topProducts.map((p, i) => (
                  <Box key={p.name}>
                    <Flex justify="space-between" align="center" mb="3px">
                      <Text fontSize="12px" fontWeight="700" color="gray.700" noOfLines={1}>
                        {i + 1}. {p.name}
                      </Text>
                      <Text fontSize="12px" fontWeight="800" color={GREEN} flexShrink={0} ml="8px">
                        {p.revenue.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                      </Text>
                    </Flex>
                    <Box bg="rgba(0,74,51,.08)" borderRadius="99px" h="6px" overflow="hidden">
                      <Box bg={GREEN} h="100%" borderRadius="99px" w={`${(p.revenue / maxProductRevenue) * 100}%`} />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Section>

          {/* Cupons usados */}
          <Section title="Cupons usados no período">
            {couponUsage.length === 0 ? (
              <Text fontSize="12px" color="gray.400">Nenhum cupom usado neste período.</Text>
            ) : (
              <Box display="flex" flexDirection="column" gap="8px">
                {couponUsage.map(({ coupon, count }) => (
                  <Flex key={coupon.id} justify="space-between" align="center">
                    <Flex align="center" gap="8px">
                      <Text fontSize="12px" fontWeight="800" color="gray.700" letterSpacing="0.03em">{coupon.code}</Text>
                      <Badge colorScheme={coupon.kind === "fixed" ? "blue" : "purple"} fontSize="9px" borderRadius="4px">
                        {coupon.kind === "fixed"
                          ? coupon.discount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
                          : `${coupon.discount}%`}
                      </Badge>
                    </Flex>
                    <Text fontSize="12px" color="gray.600">{count} uso{count !== 1 ? "s" : ""}</Text>
                  </Flex>
                ))}
              </Box>
            )}
          </Section>
        </>
      )}
    </Box>
  );
}
